import React, { useEffect, useState, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { useAppSelector } from '@/redux/store'
import { makeStyles, useTheme } from '@mui/styles'
import { flatten, uniqBy } from 'lodash'

import orgamax from 'ampersand-app'
import theme from 'theme'
import { CurrencyInput, SelectInput } from '@/modules/base/components/inputs'
import { priceKinds, receiptStates } from '@/modules/base/util/constants'
import { defaultStyles as selectInputDefaultStyles } from '@/modules/base/components/inputs/select-input/select-input.styles'
import { openDrawer, setSelectedPosition, getChartsOfAccounts } from '@/redux/ducks/taxCategories/main'
import { TaxCategoryDropdownIndicator } from '@/modules/base/components/tax-categories/tax-category-list-indicator'
import buildPositionCategory from './_helpers/buildPositionCategory'
import { findVatRateForPeriod, sortVatListByDefault } from '@/modules/transactionV2/util/vat'
import { calculatePositionBasedGross, calculatePositionBasedNet } from './_helpers/PositionWithTaxCategory'
import { formatCurrency } from '@/modules/_helpers/formatCurrency'
import { awaitableConfirmationModal } from '@/modules/base/components/modal/_helpers/awaitableConfirmationModal'
import clsx from 'clsx'
import lang from '@/lang'
// FLAG:useDocuAi — error message shown when a category does not include the row's locked VAT rate.
import { toast } from '@/modules/base/components/toast/toastConfig'

const drawerSelectOption = { label: 'Steuerkonten-Liste öffnen', value: 'openDrawer' }
const categoryCustomComponents = { DropdownIndicator: TaxCategoryDropdownIndicator }

// FLAG:useDocuAi — `compact` (invoice-review): shorter control, no bold on the
// current rate, and tightened value/indicator padding so "19 %" fits the small field.
const vatSelectStyles = (highlight, compact) => ({
  ...selectInputDefaultStyles,
  control: (base, state) => {
    const {
      isDisabled,
      isFocused,
    } = state

    const borderColor = isFocused ? theme.primary : theme.borderColor

    const styles = {
      ...base,
      background: isDisabled ? theme.backgroundColorDisabled : theme.backgroundColor,
      border: isFocused ? `1px solid ${theme.primary}` : 'none',
      borderLeft: `1px solid ${borderColor}`,
      borderRight: `1px solid ${borderColor}`,
      borderRadius: 0,
      boxShadow: 'none',
      height: compact ? '32px' : '44px',
      minHeight: compact ? '32px' : undefined,
    }

    if (highlight) {
      styles.border = `1px solid ${theme.colors.yellow} !important`
      styles.background = theme.yellowBackground
    }

    return styles
  },
  ...(compact
    ? {
      valueContainer: base => ({ ...base, padding: '0 2px' }),
      indicatorSeparator: () => ({ display: 'none' }),
      dropdownIndicator: base => ({ ...base, padding: 2 }),
    }
    : {}),
  singleValue: (styles, { data }) => {
    if (data.isCurrent && !compact) {
      return { ...styles, fontWeight: 'bold' }
    }
    return styles
  },
  option: (styles, { data }) => {
    if (data.isCurrent) {
      return { ...styles, fontWeight: 'bold' }
    }
    return styles
  },
})

const categorySelectStyles = (highlight, compact) => {
  return {
    ...selectInputDefaultStyles,
    control: (base, state) => {
      const {
        isDisabled,
        isFocused,
      } = state

      const styles = {
        ...base,
        background: isDisabled ? theme.backgroundColorDisabled : theme.backgroundColor,
        border: isFocused ? `1px solid ${theme.primary}` : 'none',
        borderRadius: `${theme.borderRadius} 0 0 ${theme.borderRadius}`,
        height: compact ? '32px' : '44px',
        minHeight: compact ? '32px' : undefined,
        boxShadow: 'none',
      }

      if (highlight) {
        styles.border = `1px solid ${theme.colors.yellow}`
        styles.background = theme.yellowBackground
      }

      return styles
    },
  }
}

const useStyles = makeStyles(theme => ({
  // FLAG:useDocuAi — `compact` (invoice-review drawer) lets the row shrink to the
  // narrow drawer: the row grows/shrinks instead of flex:1 0 0, the VAT field is
  // smaller, and the price is a fixed narrow column next to it (no overflow).
  item: props => ({
    display: 'flex',
    alignItems: 'center',
    flex: props.compact ? '1 1 auto' : '1 0 0',
    minWidth: props.compact ? 0 : undefined,
    borderRadius: theme.borderRadius,
    border: `1px solid ${theme.borderColor}`,
    height: props.compact ? 34 : 46,
  }),

  category: props => ({
    flex: '1 1 auto',
    minWidth: props.compact ? 0 : undefined,
  }),
  price: props => ({
    width: props.compact ? 88 : '24%',
    flex: props.compact ? '0 0 88px' : undefined,

    '& input': {
      border: 'none',
      textAlign: 'right',
      paddingRight: '8px',
      borderRadius: `0 ${theme.borderRadius} ${theme.borderRadius} 0`,
      height: props.compact ? 32 : 44,

      '&:focus': {
        border: `1px solid ${theme.activeInputBorder}`,
      },
    },
  }),
  vat: props => ({
    flex: props.compact ? '0 0 72px' : '0 0 100px',
  }),
  // FLAG:useDocuAi — the line's VAT AMOUNT, read-only. It is already calculated (calculate-
  // PositionBasedGross/Net), stored (positions[i].vat), and booked through bookingApi — it simply
  // was never visible. The category field (flex 1 1 auto) provides the space.
  vatAmount: {
    flex: '0 0 78px',
    padding: '0 8px',
    textAlign: 'right',
    fontSize: theme.fontSizes.small,
    color: theme.textLight,
    borderLeft: `1px solid ${theme.borderColor}`,
    lineHeight: '32px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    cursor: 'help',
  },
  customTaxCategoryOption: {
    color: theme.primary,
    fontWeight: 500,
  },
  highlight: {
    '& > input': {
      border: `1px solid ${theme.colors.yellow}`,
      background: theme.yellowBackground,
    },
  },
  hightlightSelect: {
    borderColor: theme.colors.yellow,
    background: theme.yellowBackground,
  },
  // FLAG:useDocuAi — per-line check colours from fieldMeta.positions[i]. Border only:
  // unlike the autofill highlight above these must not tint the background, so a flagged
  // value stays as readable as any other. 'error' red, 'warning' the yellow review colour.
  checkError: {
    '& > input': {
      border: `1px solid ${theme.red}`,
    },
  },
  checkWarning: {
    '& > input': {
      border: `1px solid ${theme.colors.yellow}`,
    },
  },
  checkSelectError: {
    borderColor: theme.red,
  },
  checkSelectWarning: {
    borderColor: theme.colors.yellow,
  },
}))

const getCategorySelectOptions = ({ classes, menuPlacement, loadOptions, defaultOptions, shouldHighlight, compact, onOpenTaxCategoryList }) => ({
  styles: categorySelectStyles(shouldHighlight, compact),
  placeholder: lang.taxCategoryPositions.selectInputPlaceholder,
  isClearable: true,
  backspaceRemoves: true,
  menuPlacement,
  loadOptions,
  defaultOptions,
  onOpenTaxCategoryList,
  taxCategoryListDataQsId: 'positionWithTaxCategoryItem-openTaxCategoryList',
  formatOptionLabel: (option) => {
    const { value, label } = option
    const customClass = value === 'openDrawer' ? classes.customTaxCategoryOption : ''
    return (
      <span className={`${classes.selectOption} ${customClass}`}>{label}</span>
    )
  },
})
const mapFavoriteCategoriesToOptions = (categories) => {
  return categories.map(category => {
    const { accountDescriptionCustom, id, accountNo } = category
    return {
      label: `${accountDescriptionCustom} (${accountNo})`,
      value: id,
      ...category,
    }
  })
}

const getVatSelectOptions = (menuPlacement, shouldHighlight, compact) => {
  return {
    styles: vatSelectStyles(shouldHighlight, compact),
    menuPlacement,
  }
}

export const PositionWithTaxCategoryItem = (props) => {
  const {
    disableTaxCategorySelection,
    state,
    index,
    receiptId,
    position,
    priceKind = priceKinds.GROSS,
    onChangePosition,
    dateFor,
    disabled,
    disablePrice,
    category,
    menuPlacement,
    isNewBookkeepingRecord,
    isEconomicAsset,
    filledData = [],
    ocrDataChanged,
    accountType,
    // FLAG:useDocuAi — invoice-review layout (compact) + XML lock (values
    // read-only, only the category stays editable). The *XmlLocked flags say the
    // lock comes from an XML e-invoice, which adds the "why locked" tooltip.
    compact,
    disableVat,
    vatXmlLocked,
    priceXmlLocked,
    // FLAG:useDocuAi — graded per-line verdict ('error' | 'warning' | null) plus its note,
    // resolved by the list from fieldMeta.positions[i]. Absent on every legacy render.
    vatCheckState,
    priceCheckState,
    vatCheckNote,
    priceCheckNote,
    // FLAG:useDocuAi — receipt currency code; default „€" (like CurrencyInput itself), so every
    // legacy caller renders unchanged.
    currencySymbol = '€',
    // FLAG:useDocuAi — show the line's VAT amount (review view only).
    showVatAmount = false,
    // FLAG:useDocuAi — a specific reason for the locked category (foreign currency) instead of the
    // generic "no accounting module" hint.
    taxCategoryDisabledHint,
  } = props
  const {
    isNew,
    isChanged,
    bookkeepingAccountId,
    bookkeepingAccountDescriptionCustom,
    accountVatOption,
    totalGross: positionTotalGross,
    totalNet: positionTotalNet,
    vatPercent: positionVatPercent,
    allowedVatRates,
    bookkeepingAccountNo,
  } = position

  const classes = useStyles(props)
  const dispatch = useDispatch()
  const theme = useTheme()
  const [totalGross, setTotalGross] = useState(positionTotalGross)
  const [totalNet, setTotalNet] = useState(positionTotalNet)
  const [vatPercent, setVatPercent] = useState(positionVatPercent)
  const [taxCategories, setTaxCategories] = useState([drawerSelectOption])
  const [allowedVats, setAllowedVats] = useState([])
  const [isMouting, setIsMounting] = useState(true)
  const [newBookkeeping, setNewBookkeeping] = useState(bookkeepingAccountId ? { value: bookkeepingAccountId, label: `${bookkeepingAccountDescriptionCustom} (${bookkeepingAccountNo})` } : null)
  const { favoriteCategories, categories } = useAppSelector(state => state.taxCategories.main)
  const companyData = useAppSelector(state => state.settings?.company)
  const highlightCategorySelect = !isNew && !isChanged && ((state === receiptStates.DRAFT && receiptId) || ocrDataChanged) && filledData.some((value) => ['supplierId', 'customerId'].includes(value)) && bookkeepingAccountId
  const highlightVatSelect = !isNew && !isChanged && ((state === receiptStates.DRAFT && receiptId) || ocrDataChanged) && filledData.some(value => ['vatAmount', 'vat'].includes(value))
  const getTaxCategories = async(search) => {
    const flattenCategories = uniqBy(flatten(categories.map(cat => cat.accounts)), 'accountNo')
    const result = flattenCategories.filter(fc => fc.accountDescriptionCustom.toLowerCase().includes(search.toLowerCase()) || `${fc.accountNo}`.includes(search)).map(cat => {
      return {
        ...cat,
        label: `${cat.accountDescriptionCustom} (${cat.accountNo})`,
        value: cat.id,
      }
    })

    return result
  }

  const vatList = sortVatListByDefault(orgamax.user.getValidVatList(dateFor))
  const isVatValidForDate = (value) => vatList.some(vat => parseFloat(vat.value) === parseFloat(value))

  useEffect(() => {
    dispatch(getChartsOfAccounts({ visibility: 'extended', accountType }))
    setIsMounting(false)
  }, [])

  useEffect(() => {
    if (priceKind !== priceKinds.GROSS) return
    const calculatedPosition = calculatePositionBasedGross(totalGross, vatPercent)

    const updatedPosition = { ...props.position, ...calculatedPosition }

    onChangePosition(updatedPosition)
  }, [totalGross, vatPercent])

  useEffect(() => {
    if (priceKind !== priceKinds.NET) return
    const calculatedPosition = calculatePositionBasedNet(totalNet, vatPercent)

    const updatedPosition = { ...props.position, ...calculatedPosition }

    onChangePosition(updatedPosition)
  }, [totalNet, vatPercent])

  useEffect(() => {
    if (positionTotalGross !== totalGross) { setTotalGross(positionTotalGross) }
    if (positionTotalNet !== totalNet) { setTotalNet(positionTotalNet) }

    // F6: when the VAT is locked (XML invoice), NEVER normalize it to the account default —
    // keep the detected rate exactly. `disableVat` disables the input, but without this guard
    // the effect still rewrote an out-of-range rate on render. Only unlocked positions snap
    // to an allowed rate.
    let vatPercent = positionVatPercent
    if (!disableVat) {
      const allowedForDate = (allowedVatRates?.values ?? []).filter(isVatValidForDate)
      const isAllowedForDate = (value) => allowedForDate.some(allowed => parseFloat(allowed) === parseFloat(value))
      const fallbackVat = isAllowedForDate(allowedVatRates?.default) ? allowedVatRates.default : allowedForDate[0]
      if (allowedForDate.length && !isAllowedForDate(positionVatPercent)) {
        vatPercent = fallbackVat
      }
    }

    setVatPercent(vatPercent)
  }, [priceKind, positionVatPercent, positionTotalNet, positionTotalGross])

  useEffect(() => {
    const mappedFavoriteCategories = mapFavoriteCategoriesToOptions(favoriteCategories)

    const categories = [
      ...mappedFavoriteCategories,
    ]
    // add selected category as option if it isn't in favorites
    if (bookkeepingAccountId) {
      const existingCategory = mappedFavoriteCategories.find(category => category.value === bookkeepingAccountId)
      if (!existingCategory) {
        const newCategory = {
          value: bookkeepingAccountId,
          label: bookkeepingAccountDescriptionCustom,
          vatDefaultRate: positionVatPercent,
          vatOption: accountVatOption,
          allowedVatRates: category?.allowedVatRates,
          accountNo: bookkeepingAccountNo,
        }
        categories.unshift(newCategory)
        setAllowedVatsByValidDate(newCategory?.allowedVatRates?.values)
      } else {
        setAllowedVatsByValidDate(existingCategory?.allowedVatRates?.values)
      }
    }

    setTaxCategories([drawerSelectOption, ...categories])
    setNewBookkeeping(bookkeepingAccountId ? { value: bookkeepingAccountId, label: `${bookkeepingAccountDescriptionCustom} (${bookkeepingAccountNo})` } : null)
  }, [favoriteCategories, bookkeepingAccountId])

  useEffect(() => {
    // Check if company is VAT-exempt (has defaultRevenueAccount)
    const isVatExempt = companyData?.defaultRevenueAccount !== null && companyData?.defaultRevenueAccount !== ''
    if (!isVatExempt && (isNewBookkeepingRecord || !isMouting)) {
      const replacementVat = findVatRateForPeriod(vatList, orgamax.user.getVatList(dateFor), vatPercent)
      if (replacementVat != null) {
        setVatPercent(replacementVat)
      }
    }
    if (newBookkeeping?.value) {
      // refresh available vat percents options of current category
      setAllowedVatsByValidDate(allowedVatRates?.values)
    }
  }, [dateFor, companyData])

  const setAllowedVatsByValidDate = (allowedVatList = []) => {
    setAllowedVats((allowedVatList ?? []).filter(isVatValidForDate))
  }

  const handleOpenTaxCategoryList = () => {
    dispatch(setSelectedPosition({ ...position, index }))
    dispatch(openDrawer())
  }

  const handleChange = async(option) => {
    dispatch(setSelectedPosition({ ...position, index }))
    // Check if company is VAT-exempt (has defaultRevenueAccount)
    const isVatExempt = companyData?.defaultRevenueAccount !== null && companyData?.defaultRevenueAccount !== ''
    const newData = option
      // FLAG:useDocuAi — compact === review row: preserve a legitimate 0% current rate.
      // Legacy (non-compact) keeps the f2ac6a682f `||` fallback.
      ? buildPositionCategory(option, dateFor, compact)
      : {
        bookkeepingAccount: {},
        bookkeepingAccountId: null,
        bookkeepingAccountDescriptionCustom: null,
        bookkeepingAccountNo: null,
        accountCategory: option?.accountCategory || [],
        vatOption: 0,
        vatDefaultRate: isVatExempt ? 0 : (option === null ? vatPercent : vatList.find(vatItem => vatItem.isCurrent && vatItem.isDefault).value),
        // FLAG:useDocuAi — clearing the category must clear its rate restriction with it. This
        // object does not name `allowedVatRates`, so the spread below kept the deleted account's
        // list on the position — and the normalization effect above went on enforcing it: after
        // "0% → 19% account → confirm → delete the account", picking 0% again was applied and then
        // immediately rewritten back to 19%, the only allowed rate of an account that is gone.
        // No account means no allowed-rate list. Review row only (compact); the legacy row keeps
        // the previous object byte-for-byte.
        ...(compact ? { allowedVatRates: null } : {}),
      }

    const allAllowedVatRates = option?.allowedVatRates?.values || []
    setAllowedVatsByValidDate(allAllowedVatRates)

    if (option && option.value === 'openDrawer') return dispatch(openDrawer())

    // FLAG:useDocuAi — on an XML e-invoice, the line's
    // VAT rate is a receipt fact (disableVat) and is
    // restored from the analysis on save anyway. A category that does not support this rate is
    // therefore not a matter of preference, but simply wrong: previously it was assigned anyway and
    // the rate silently retained — leaving the line with a 19% category at 7% without telling anyone.
    // Reject it and name the reason instead of creating an unbookable combination.
    // No verdict without data: a category without allowedVatRates remains valid. Clearing the
    // category (option === null) does too — it creates no incorrect assignment. Applies only in the
    // review (compact) to rate-locked lines; everything else remains unchanged.
    if (compact && disableVat && option) {
      const supportedRates = option?.allowedVatRates?.values
      const currentRate = props.position.vatPercent
      const rateIsSupported = !Array.isArray(supportedRates) || !supportedRates.length
        || currentRate === null || currentRate === undefined
        || supportedRates.includes(currentRate)
      if (!rateIsSupported) {
        toast.showToast({
          type: 'error',
          message: lang.taxCategoryPositions.categoryVatMismatch(currentRate, option.accountDescriptionCustom),
        })
        return
      }
    }

    // FLAG:useDocuAi — §4.2 (review only, compact): the inline category pick must
    // never silently rewrite the line's VAT. Locked lines (disableVat) and
    // rate-less accounts keep the detected rate; otherwise a rate change needs an
    // explicit confirm — declining assigns only the category. Legacy (non-compact)
    // keeps the old silent overwrite.
    let nextVatPercent = newData.vatDefaultRate
    if (compact) {
      const currentRate = props.position.vatPercent
      const rateWouldChange = currentRate !== null && currentRate !== undefined && nextVatPercent !== currentRate
      if (rateWouldChange) {
        let keepRate = disableVat || nextVatPercent === null || nextVatPercent === undefined
        if (!keepRate) {
          keepRate = !(await awaitableConfirmationModal({
            isOpen: true,
            title: lang.documents.vatChangeConfirm.title,
            description: lang.documents.vatChangeConfirm.description(currentRate, nextVatPercent),
          }))
        }
        if (keepRate) {
          nextVatPercent = currentRate
          // FLAG:useDocuAi — the preserved rate must STAY preserved: otherwise `newData` carries the
          // NEW category's allowedVatRates, and the normalization effect above (dependencies
          // positionTotalNet/positionTotalGross) silently resets any rate outside
          // `allowedVatRates.values` to its `default` on the next amount correction — making the
          // just-rejected change happen after all. Like buildBulkAssignedPosition
          // (position-with-tax-category-list), the line retains its previous allowedVatRates.
          newData.allowedVatRates = props.position.allowedVatRates
        }
      }
    }

    const updatedPosition = { ...props.position, ...newData, vatPercent: nextVatPercent, accountVatOption: newData.vatOption, isChanged: true }
    setNewBookkeeping(option?.id ? { value: option.id, label: `${option.accountDescriptionCustom} (${option.accountNo})` } : null)
    onChangePosition(updatedPosition)
  }

  const getSelectInputOptions = () => {
    const toOption = vat => ({ label: vat.name, value: vat.value, isCurrent: vat.isCurrent })
    if (allowedVats && allowedVats.length) {
      return vatList.filter(vat => allowedVats.some(allowed => parseFloat(allowed) === parseFloat(vat.value))).map(toOption)
    }
    return vatList.map(toOption)
  }

  // FLAG:useDocuAi — derive from LOCAL state, not props.position.vat: the effect above reports an
  // amount/rate change upwards first and returns it one render later, so the display otherwise
  // visibly lagged behind. Exactly the same calculation as the stored value — the same function and
  // the same per-line rounding.
  const positionVatAmount = useMemo(() => {
    const calculated = priceKind === priceKinds.GROSS
      ? calculatePositionBasedGross(totalGross, vatPercent)
      : calculatePositionBasedNet(totalNet, vatPercent)
    return calculated.vat
  }, [priceKind, totalGross, totalNet, vatPercent])

  const currencyInput = useMemo(() => {
    const isPriceKindGross = priceKind === priceKinds.GROSS
    const inputAutoFilled = !isNew && !isChanged && (state === receiptStates.DRAFT || !receiptId || ocrDataChanged)
      && (filledData.includes('priceTotal') || filledData.includes('priceNet'))

    return (
      <CurrencyInput
        name='price'
        dataQsId='positionWithTaxCategoryItem-price'
        autoFocus={!!isNew}
        disabled={disabled}
        customClass={clsx(
          classes.price,
          inputAutoFilled && classes.highlight,
          priceCheckState === 'error' && classes.checkError,
          priceCheckState === 'warning' && classes.checkWarning,
        )}
        value={isPriceKindGross ? totalGross : totalNet}
        symbol={currencySymbol}
        onBlur={(value) => isPriceKindGross ? setTotalGross(value) : setTotalNet(value)}
        fillAnimationOptions={{ show: inputAutoFilled, color: theme.colors.yellow }}
        readOnly={disablePrice || isEconomicAsset}
        tooltip={priceXmlLocked
          ? lang.documents.xmlLockedValueHint
          : (priceCheckState ? priceCheckNote || undefined : undefined)}
      />
    )
  }, [isNew, isChanged, state, priceKind, totalGross, totalNet, disabled, disablePrice, filledData, isEconomicAsset, priceXmlLocked, priceCheckState, priceCheckNote, currencySymbol])

  return (
    <div className={classes.item}>
      <SelectInput
        name='category'
        dataQsId='positionWithTaxCategoryItem-category'
        disabled={disabled}
        allowCreate={false}
        containerClass={classes.category}
        fillAnimationOptions={{ show: highlightCategorySelect, color: theme.colors.yellow }}
        selectContainerClass={highlightCategorySelect && classes.hightlightSelect}
        customSelectOptions={getCategorySelectOptions({
          classes,
          menuPlacement,
          loadOptions: getTaxCategories,
          defaultOptions: taxCategories,
          shouldHighlight: highlightCategorySelect,
          compact,
          onOpenTaxCategoryList: handleOpenTaxCategoryList,
        })}
        customComponents={categoryCustomComponents}
        value={newBookkeeping}
        handleChange={handleChange}
        readOnly={isEconomicAsset || disableTaxCategorySelection}
        hint={disableTaxCategorySelection
          ? (taxCategoryDisabledHint || lang.taxCategoryPositions.selectInputDisabledHint)
          : ''}
      />
      <SelectInput
        name='vat'
        dataQsId='positionWithTaxCategoryItem-vat'
        notAsync
        disabled={disabled || disableVat}
        tooltip={vatXmlLocked
          ? lang.documents.xmlLockedValueHint
          : (vatCheckState ? vatCheckNote || undefined : undefined)}
        containerClass={classes.vat}
        fillAnimationOptions={{ show: highlightVatSelect, color: theme.colors.yellow }}
        selectContainerClass={clsx(
          highlightVatSelect && classes.hightlightSelect,
          vatCheckState === 'error' && classes.checkSelectError,
          vatCheckState === 'warning' && classes.checkSelectWarning,
        ) || undefined}
        allowCreate={false}
        value={vatPercent}
        options={getSelectInputOptions()}
        handleChange={({ value }) => { setVatPercent(value) }}
        customSelectOptions={getVatSelectOptions(menuPlacement, highlightVatSelect, compact)}
        // FLAG:useDocuAi — `disabled` above is DEAD on SelectInput: the component never reads a
        // `disabled` prop, it maps only `readOnly` onto react-select's isDisabled (see
        // select-input.js). So the XML lock was cosmetic — the tooltip said "must not be changed"
        // while the menu still opened and a pick recomputed the line's VAT (19% → 7%
        // rewrote 24,24 € to 8,93 €). The save discarded it (enforceXmlLockedFields), which is
        // worse, not better: the reviewer judged a state the document never had. Price and
        // category already lock through `readOnly`; the VAT select simply never joined them.
        // `disableVat` is passed ONLY by the review (compact) render, so flag-OFF this stays
        // exactly `isEconomicAsset`.
        readOnly={isEconomicAsset || Boolean(disableVat)}
      />
      {showVatAmount && (
        <div
          className={classes.vatAmount}
          data-qs-id='positionWithTaxCategoryItem-vatAmount'
          title={lang.taxCategoryPositions.positionVatAmountHint}
        >
          {formatCurrency(positionVatAmount, { symbol: currencySymbol })}
        </div>
      )}
      {currencyInput}
    </div>
  )
}

export default PositionWithTaxCategoryItem
